// Auth.js
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSafeRedirect } from "./inviteFlow";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { AlertCircle } from "lucide-react";
import { API_BASE } from "./api";

const isRegisterPath = (pathname) =>
  pathname === "/register" || pathname === "/signup";

const Auth = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(() => !isRegisterPath(pathname));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dealCode, setDealCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordResetAvailable, setPasswordResetAvailable] = useState(false);
  const { login, register, sessionExpired } = useAuth();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/auth/password-reset-status`)
      .then((res) => {
        if (!cancelled) {
          setPasswordResetAvailable(Boolean(res.data?.available));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPasswordResetAvailable(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setIsLogin(!isRegisterPath(pathname));
  }, [pathname]);

  // Check for deal code in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const deal = urlParams.get("deal");
    if (deal) {
      setDealCode(deal);
      setIsLogin(false); // Switch to register mode for deal codes
    }
  }, []);

  const validateForm = () => {
    if (!email || !password) {
      setError("Email and password are required.");
      return false;
    }
    if (dealCode && dealCode.length < 5) {
      setError("Deal code must be at least 5 characters long.");
      return false;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return false;
    }
    setError("");
    return true;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, dealCode);
      }
      navigate(getSafeRedirect(search), { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "Authentication failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form-container">
        {dealCode && !isLogin && (
          <div className="deal-banner">
            🎉 You're using deal code: <strong>{dealCode}</strong>
          </div>
        )}

        {sessionExpired && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            <AlertCircle size={16} />
            <span>Session expired — please log in again.</span>
          </div>
        )}

        <h2 className="auth-title">{isLogin ? "Login" : "Register"}</h2>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              required
            />
          </div>

          {!isLogin && !dealCode && (
            <div className="form-group">
              <label htmlFor="dealCode">Deal Code (optional)</label>
              <input
                type="text"
                id="dealCode"
                value={dealCode}
                onChange={(e) => setDealCode(e.target.value.toUpperCase())}
                className="auth-input"
                placeholder="Enter deal code if you have one"
              />
            </div>
          )}

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button type="submit" className="auth-button" disabled={isLoading}>
            {isLoading ? "Processing..." : isLogin ? "Login" : "Register"}
          </button>
        </form>

        {isLogin && passwordResetAvailable ? (
          <Link to="/forgot-password" className="toggle-auth-mode">
            Forgot password?
          </Link>
        ) : null}

        {!dealCode && (
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="toggle-auth-mode"
          >
            {isLogin
              ? "Don't have an account? Register"
              : "Already have an account? Login"}
          </button>
        )}
      </div>
    </div>
  );
};

export default Auth;
